import { Injectable, inject } from '@angular/core';
import { calculateDistance } from '@functions';
import {
  DetectionService,
  LogicService,
  MessageService,
  RenderService,
  SimulationService,
} from '@services';
import '@tensorflow/tfjs-backend-webgl';
import { ExtraCorporealKnotMetricsService } from './extracorporeal-knot-metrics.service';

const MIN_DISTANCE_BETWEEN_TISSUE_AND_PUSHER = 100;
const MIN_DISTANCE_BETWEEN_TISSUE_AND_KNOT = 100;
const CONFIDENCE_KNOT_PUSHER = 0.1;
const CONFIDENCE_KNOT = 0.2;

@Injectable({
  providedIn: 'root',
})
export class SharedService {
  private detectionService = inject(DetectionService);
  private extracorporealKnotMetricsService = inject(
    ExtraCorporealKnotMetricsService,
  );
  private logicService = inject(LogicService);
  private messageService = inject(MessageService);
  private renderService = inject(RenderService);
  private simulationService = inject(SimulationService);

  cycleWorkerId: string = undefined;

  piercingClassWorkerId: string = undefined;

  isInTissue: boolean;

  knotPusherData = {
    isFirstKnotdetected: false,
    pusherTissueClose: false,
    pusherTissueFar: false,
    pusherComes: false,
    numberKnotsDone: 0,
  };

  checkSample(): Promise<boolean> {
    return new Promise<boolean>((resolve, reject) => {
      try {
        const tissue = this.detectionService.getBox(
          'tissue',
          this.cycleWorkerId,
        );

        const tissueWidth = tissue?.max.x - tissue?.min.x;
        const tissueHeigth = tissue?.max.y - tissue?.min.y;
        const isOrientationVertical = tissueWidth < 0.8 * tissueHeigth;

        this.logicService.checkSamplePosition({
          sample: 'tissue',
          workerId: this.cycleWorkerId,
          confidence: 0.5,
          width: 640,
          height: 400,
          moveX: -100,
          moveY: 100,
          image: 'overlays/suture-block.svg',
          additionalCondition: isOrientationVertical,
        });
        resolve(this.simulationService.state() === 'simulation');
      } catch (error) {
        reject(error);
      }
    });
  }

  checkInstruments(): Promise<boolean> {
    return new Promise<boolean>((resolve, reject) => {
      try {
        this.logicService
          .checkInstrumentsPosition([
            {
              id: 'toolLeft',
              workerId: this.cycleWorkerId,
              confidence: 0.2,
              moveX: -350,
              radius: 90,
              image: 'overlays/instrument-left.svg',
            },
            {
              id: 'toolRight',
              workerId: this.cycleWorkerId,
              confidence: 0.2,
              moveX: 170,
              radius: 90,
              image: 'overlays/instrument-right.svg',
            },
          ])
          .then((instrumentsConfirmed) => {
            if (!instrumentsConfirmed) {
              this.messageService.showDetectionMessage({
                text: 'Bring your instruments together in the camera view',
                identifier: 'checkInstruments',
              });
            }
            resolve(instrumentsConfirmed);
          });
      } catch (error) {
        reject(error);
      }
    });
  }

  piercePenrose(): Promise<boolean> {
    return new Promise<boolean>((resolve, reject) => {
      try {
        const penrose = this.detectionService.getBox(
          'tissue',
          this.cycleWorkerId,
        );

        const piercingInside = this.detectionService.getBox(
          'needleInTissue',
          this.cycleWorkerId,
        );

        this.messageService.showDetectionMessage({
          text: 'Pass the needle through the dots',
          identifier: 'piercePenrose',
        });

        if (penrose) {
          this.renderService.addDynamic({
            overlay: {
              type: 'image',
              image: 'overlays/pierce-penrose.svg',
              x: penrose.max.x + 100,
              y: penrose.min.y - 53,
            },
          });
        }
        this.isInTissue = piercingInside
          ? piercingInside?.center.x <
            penrose?.min.x + (penrose?.max.x - penrose?.min.x) * 0.7
          : this.isInTissue;

        const isPierced =
          this.detectionService.getRegressionTrend(
            'isPierced',
            this.piercingClassWorkerId,
            3,
            0.7,
          ) &&
          !this.detectionService.getRegressionTrend(
            'isNotPierced',
            this.piercingClassWorkerId,
            3,
            0.7,
          ) &&
          this.isInTissue;

        resolve(isPierced);
      } catch (error) {
        reject(error);
      }
    });
  }

  pullSuture(): Promise<boolean> {
    return new Promise<boolean>((resolve, reject) => {
      try {
        const penrose = this.detectionService.getBox(
          'tissue',
          this.cycleWorkerId,
        );

        if (penrose) {
          this.renderService.addDynamic({
            overlay: {
              type: 'image',
              image: 'overlays/pull-suture.svg',
              x: penrose.min.x - 200,
              y: penrose.center.y,
            },
          });
        }

        const piercingInside = this.detectionService.getBox(
          'needleInTissue',
          this.cycleWorkerId,
        );

        const isSuturePulled =
          this.detectionService.getRegressionTrend(
            'isPulling',
            this.piercingClassWorkerId,
            4,
            0.95,
          ) && !piercingInside;

        const isSutureNotPierced = !this.detectionService.getRegressionTrend(
          'isPierced',
          this.piercingClassWorkerId,
          4,
          0.7,
        );

        const isNeedleInTissueAbsent =
          this.detectionService.getNumberOfBoxEmptys(
            'needleInTissue',
            this.cycleWorkerId,
            5,
          ) === 5;

        const isPulled =
          isSuturePulled && isSutureNotPierced && isNeedleInTissueAbsent;

        resolve(isPulled);
      } catch (error) {
        reject(error);
      }
    });
  }

  tieKnots(): Promise<boolean> {
    return new Promise<boolean>((resolve, reject) => {
      try {
        const knots = this.detectionService.getAllBoxInstances(
          'knot',
          this.cycleWorkerId,
        );
        const [knotPusher, tissue] = this.detectionService.getBoxes(
          ['knotPusher', 'tissue'],
          this.cycleWorkerId,
        );

        const distPusherTissue =
          knotPusher && tissue
            ? calculateDistance(
                { x: knotPusher.center.x, y: knotPusher.center.y },
                { x: tissue.center.x, y: tissue.center.y },
              )
            : null;

        const isKnotPusherCloseToTissue =
          knotPusher && tissue && distPusherTissue
            ? knotPusher.confidence > CONFIDENCE_KNOT_PUSHER &&
              distPusherTissue < MIN_DISTANCE_BETWEEN_TISSUE_AND_PUSHER
            : null;
        if (isKnotPusherCloseToTissue) {
          this.knotPusherData.pusherComes = true;
        }
        const isKnotTied =
          (knots.at(-1) &&
            this.knotPusherData.pusherComes &&
            knots.at(-1).confidence > CONFIDENCE_KNOT) ||
          (this.knotPusherData.pusherComes && this.knotMiddleCheck());

        if (isKnotTied && !this.knotPusherData.isFirstKnotdetected) {
          this.messageService.showDetectionMessage({
            text: 'Knot tying detected! Keep up the good work by tying any remaining knots',
            identifier: 'knotDetection',
          });
          this.knotPusherData.isFirstKnotdetected = true;
          this.knotPusherData.pusherComes = false;
          this.knotPusherData.numberKnotsDone += 1;
        }
        resolve(isKnotTied);
      } catch (error) {
        reject(error);
      }
    });
  }

  cutThreads(): Promise<boolean> {
    return new Promise<boolean>((resolve, reject) => {
      try {
        const isThreadCut = false;

        resolve(isThreadCut);
      } catch (error) {
        reject(error);
      }
    });
  }

  isThresholdReached(arrayBoolCondition: boolean[], BoolCondition: boolean) {
    arrayBoolCondition.shift();
    arrayBoolCondition.push(BoolCondition);
    const numberTrue = this.countNumberOfTrue(arrayBoolCondition);
    const conditionTask = numberTrue > 7;
    return conditionTask;
  }

  knotMiddleCheck() {
    const knots = this.detectionService.getAllBoxInstances(
      'knot',
      this.cycleWorkerId,
    );
    const tissue = this.detectionService.getBox('tissue', this.cycleWorkerId);

    for (let i = 0; i < knots.length; i++) {
      const knotTissueDist =
        knots.at(i) && tissue
          ? calculateDistance(
              { x: knots.at(i).center.x, y: knots.at(i).center.y },
              { x: tissue.center.x, y: tissue.center.y },
            )
          : null;
      return (
        knots.at(i) &&
        knotTissueDist > MIN_DISTANCE_BETWEEN_TISSUE_AND_KNOT &&
        knots.at(i).confidence > CONFIDENCE_KNOT
      );
    }
    return false;
  }

  countNumberOfTrue(arrayConditions: boolean[]): number {
    return arrayConditions.reduce((count, value) => count + (value ? 1 : 0), 0);
  }

  toolTracking() {
    this.extracorporealKnotMetricsService.trackLeftTool(
      this.detectionService.getBox('toolLeft', this.cycleWorkerId),
    );
    this.extracorporealKnotMetricsService.trackRightTool(
      this.detectionService.getBox('toolRight', this.cycleWorkerId),
    );
  }
}
